/**
 * Account save sync — localStorage ↔ public.game_saves.
 *
 * Spec: docs/superpowers/specs/2026-08-23-account-save-sync-design.md
 * Mapping half (pure, unit-tested): collect/apply/wipe over the synced keys.
 * Engine half (SaveSyncEngine): debounced write-through push, login/logout/
 * session-restore orchestration. Last-write-wins by design — no merging.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { isFeatureFlag } from '../config/featureFlags';
import { isAchievementId } from '../config/achievements';
import { asOrbState, type OrbState } from '../config/varietyOrb';
import {
  asDiamondState,
  asMoonstoneState,
  asEvilOrbState,
  asTokenOfShroomState,
  asLightOrbState,
  type DiamondState,
  type MoonstoneState,
  type EvilOrbState,
  type TokenOfShroomState,
  type LightOrbState,
  parsePeaceful,
  type PeacefulState,
} from '../config/hub';
import { parseEffects, type EffectRecord } from '../config/effects';
import {
  parsePotions,
  parsePotionShop,
  type PotionKind,
  type PotionShopState,
} from '../config/potions';
import { HUB_LEVELS } from '../config/hubLevels';
import { parseQuests, type QuestState } from '../config/quests';
import { MAX_RANK } from '../services/rankMath';
import { AVATAR_ICONS_KEY, isShopAvatarCode } from '../config/avatars';
import { parseStats, type LifetimeStats } from '../config/lifetimeStats';
export interface StorageLike {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

/** Save FORMAT version (game_saves.save_version). Bump on payload changes. */
export const SAVE_VERSION = 1;
/** Cap on synced score entries — bounds the signup metadata payload. */
export const MAX_SYNCED_SCORES = 500;

const KEYS = {
  stars: 'hambi.stars',
  unlocked: 'hambi.unlocked',
  coins: 'hambi.coins',
  entered: 'hambi.entered',
  owned: 'hambi.owned',
  granted: 'hambi.granted',
  challenge: 'hambi.challenge',
  grantedSeen: 'hambi.grantedSeen',
  equipped: 'hambi.equipped',
  evolved: 'hambi.evolved',
  bagOrderedAt: 'hambi.bagOrderedAt',
  bags: 'hambi.bags',
  scores: 'hambi.scores',
  flags: 'hambi.flags',
  rank: 'hambi.rank',
  exp: 'hambi.exp',
  achievements: 'hambi.achievements',
  losses: 'hambi.losses',
  waves: 'hambi.waves',
  orb: 'hambi.orb',
  // The Hub (spec 2026-09-02-the-hub-design.md).
  hub: 'hambi.hub',
  diamond: 'hambi.diamond',
  moonstone: 'hambi.moonstone',
  debris: 'hambi.debris',
  capycoins: 'hambi.capycoins',
  capsuleOrderedAt: 'hambi.capsuleOrderedAt',
  capsules: 'hambi.capsules',
  goldstone: 'hambi.goldstone',
  // The Golden Takeout Bag (spec 2026-09-26-golden-takeout-design.md).
  goldenBagOrderedAt: 'hambi.goldenBagOrderedAt',
  goldenBags: 'hambi.goldenBags',
  // The Event Archive (spec 2026-10-04-archive-and-skip-design.md).
  capystone: 'hambi.capystone',
  capyBest: 'hambi.capyBest',
  darkCoins: 'hambi.darkCoins',
  // The Dark Shadows, levels 3–5 (spec 2026-10-07-dark-shadows-three-levels-design.md).
  darkShards: 'hambi.darkshard',
  evilOrb: 'hambi.evilorb',
  tokenOfShroom: 'hambi.tokenofshroom',
  // Above the Clouds (spec 2026-10-08-above-the-clouds-design.md).
  lightCoins: 'hambi.lightCoins',
  lightShards: 'hambi.lightshard',
  // The Light Orb and the Origin chests (spec 2026-10-13).
  lightOrb: 'hambi.lightorb',
  darkChests: 'hambi.darkchests',
  lightChests: 'hambi.lightchests',
  // The Fungi Forest's items (spec 2026-10-24).
  shrooms: 'hambi.shroom',
  snowshrooms: 'hambi.snowshroom',
  effects: 'hambi.effects',
  potions: 'hambi.potions',
  potionShop: 'hambi.potionShop',
  /** The Moon's Peaceful track (spec Revision 6). */
  hubPeaceful: 'hambi.hubPeaceful',
  // Quests (spec 2026-09-08-quests-and-potions-design.md).
  quests: 'hambi.quests',
  stats: 'hambi.stats',
  // Avatar Shop icons the player has bought (spec 2026-09-21-avatar-shop-design.md).
  avatarIcons: AVATAR_ICONS_KEY,
} as const;

/** Every account-portable key. Audio settings and hambi.user stay device-local. */
export const SYNCED_KEYS: readonly string[] = Object.values(KEYS);

export interface ScoreEntry {
  levelId: number;
  score: number;
  at: number;
}

export interface SavePayload {
  saveVersion: number;
  coins: number;
  unlocked: number;
  stars: Record<string, number>;
  entered: number[];
  owned: string[];
  granted: string[];
  challenge: string[];
  grantedSeen: string[];
  equipped: string[];
  evolved: string[];
  bagOrderedAt: number | null;
  bags: number;
  scores: ScoreEntry[];
  flags: string[];
  rank: number;
  exp: number;
  achievements: string[];
  losses: number;
  waves: number;
  /** Where the one Variety Orb is: '' | 'stored' | 'opened' (see config/varietyOrb). */
  orb: OrbState;
  /** Highest hub level won (0..HUB_LEVELS.length). */
  hub: number;
  /** Where the one Diamond is: '' | 'stored' | 'used' (see config/hub). */
  diamond: DiamondState;
  /** Where the one Moon Stone is: '' | 'stored' | 'used' (see config/hub). */
  moonstone: MoonstoneState;
  /** Space Debris on the Storage shelf — one per Rocket Rage win. */
  debris: number;
  /** Capycoins — the Capybara Event's currency (see config/capybaraEvent). */
  capycoins: number;
  /** Epoch ms the spinning capsule was bought, or null when none is. */
  capsuleOrderedAt: number | null;
  /** Ready-but-unopened capsules on the Storage shelf. */
  capsules: number;
  /** Golden Stones on the Storage shelf — one per King Hambi K–O. */
  goldstone: number;
  /** Epoch ms the cooking Golden Takeout Bag was ordered, or null when none is. */
  goldenBagOrderedAt: number | null;
  /** Ready-but-unopened Golden Takeout Bags on the Storage shelf. */
  goldenBags: number;
  /** Capystones on the Storage shelf — one per finished archive Capy Run. */
  capystone: number;
  /** Best archive Capy Run score, or null when no archive run has finished. */
  capyBest: number | null;
  /** Dark Hambi Coins — the Origin Event's currency, paid by every Origin win. */
  darkCoins: number;
  /** Dark Shards on the Storage shelf — one per Dark Forest win. */
  darkShards: number;
  /** The Evil Orb: '' or 'stored' (see config/hub EvilOrbState). */
  evilOrb: EvilOrbState;
  /** The Token of Shroom: '' or 'stored' (spec 2026-10-27), the Evil Orb's rule. */
  tokenOfShroom: TokenOfShroomState;
  /** Light Hambi Coins — Above the Clouds' currency, paid by every win there. */
  lightCoins: number;
  /** Light Shards on the Storage shelf — one per Sky Islands win. */
  lightShards: number;
  /** The Light Orb: '', 'stored' or 'taken' (see config/hub LightOrbState). */
  lightOrb: LightOrbState;
  /** Dark Chests on the Storage shelf — a 25% drop on a Dark Shadows win. */
  darkChests: number;
  /** Light Chests on the Storage shelf — a 25% drop on an Above the Clouds win. */
  lightChests: number;
  /** Shrooms on the Storage shelf — a 25% drop from Mushy Mayhem on. */
  shrooms: number;
  /** Snowshrooms on the Storage shelf — a 25% drop on Icy Photosynthesis. */
  snowshrooms: number;
  /** Running timed effects (see config/effects). */
  effects: EffectRecord[];
  /** Stored, unused potions. */
  potions: PotionKind[];
  /** The Potion Shop's SOLD record for its cycle, or null. */
  potionShop: PotionShopState | null;
  /** The Moon's Peaceful track ({ on, hub, stars }), or null when never touched. */
  hubPeaceful: PeacefulState | null;
  /** The daily/weekly quest periods (see config/quests). */
  quests: QuestState;
  /** Lifetime play counters the quests measure (see config/lifetimeStats). */
  stats: LifetimeStats;
  /** Avatar Shop icons bought, as codes (see config/avatars). */
  avatarIcons: string[];
}

/** snake_case mirror of the game_saves columns (sans updated_at). */
export interface GameSaveRow {
  user_id: string;
  save_version: number;
  coins: number;
  unlocked: number;
  stars: Record<string, number>;
  entered: number[];
  owned: string[];
  granted: string[];
  challenge: string[];
  granted_seen: string[];
  equipped: string[];
  evolved: string[];
  bag_ordered_at: number | null;
  bags: number;
  scores: ScoreEntry[];
  flags: string[];
  rank: number;
  exp: number;
  achievements: string[];
  losses: number;
  waves: number;
  orb: string;
  hub: number;
  diamond: string;
  moonstone: string;
  debris: number;
  capycoins: number;
  capsule_ordered_at: number | null;
  capsules: number;
  goldstone: number;
  golden_bag_ordered_at: number | null;
  golden_bags: number;
  capystone: number;
  capy_best: number | null;
  dark_coins: number;
  dark_shards: number;
  evil_orb: string;
  token_of_shroom: string;
  light_coins: number;
  light_shards: number;
  light_orb: string;
  dark_chests: number;
  light_chests: number;
  shrooms: number;
  snowshrooms: number;
  effects: unknown;
  potions: unknown;
  potion_shop: unknown;
  hub_peaceful: unknown;
  quests: unknown;
  stats: unknown;
  avatar_icons: string[];
}

/**
 * The most coins (or Dark Hambi Coins) a save may hold. The columns are 32-bit
 * `int` and the sync upserts the WHOLE row, so one balance past 2 147 483 647
 * would make EVERY save fail for that player — which an admin's billion-coin
 * `/give` (2026-10-06) can reach in three gifts. Same ceiling the signup
 * trigger already clamps to; `applyGive` holds a gift to it as well.
 */
export const COIN_BALANCE_CAP = 2_000_000_000;

function nonNegInt(raw: string | null, fallback: number, min = 0): number {
  const n = raw != null ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) && n >= min ? n : fallback;
}

function jsonArray<T>(raw: string | null, keep: (x: unknown) => x is T): T[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? arr.filter(keep) : [];
  } catch {
    return [];
  }
}

const isString = (x: unknown): x is string => typeof x === 'string';
const isNumber = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const isScore = (x: unknown): x is ScoreEntry => {
  if (typeof x !== 'object' || x === null) return false;
  const e = x as Record<string, unknown>;
  return isNumber(e.levelId) && isNumber(e.score) && isNumber(e.at);
};

/** Read the synced keys into a payload, tolerating corrupt values (defaults). */
export function collectLocalSave(storage: StorageLike): SavePayload {
  let stars: Record<string, number> = {};
  try {
    const parsed = JSON.parse(storage.getItem(KEYS.stars) ?? '{}') as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      stars = Object.fromEntries(
        Object.entries(parsed as Record<string, unknown>).filter(([, v]) => isNumber(v)),
      ) as Record<string, number>;
    }
  } catch {
    stars = {};
  }
  const bagRaw = storage.getItem(KEYS.bagOrderedAt);
  const capsuleRaw = storage.getItem(KEYS.capsuleOrderedAt);
  const capsuleN = capsuleRaw != null ? parseInt(capsuleRaw, 10) : NaN;
  const bagN = bagRaw != null ? parseInt(bagRaw, 10) : NaN;
  const goldenRaw = storage.getItem(KEYS.goldenBagOrderedAt);
  const goldenN = goldenRaw != null ? parseInt(goldenRaw, 10) : NaN;
  const scores = jsonArray(storage.getItem(KEYS.scores), isScore)
    .sort((a, b) => a.at - b.at)
    .slice(-MAX_SYNCED_SCORES);
  return {
    saveVersion: SAVE_VERSION,
    coins: nonNegInt(storage.getItem(KEYS.coins), 0),
    unlocked: nonNegInt(storage.getItem(KEYS.unlocked), 1, 1),
    stars,
    entered: jsonArray(storage.getItem(KEYS.entered), isNumber),
    owned: jsonArray(storage.getItem(KEYS.owned), isString),
    granted: jsonArray(storage.getItem(KEYS.granted), isString),
    challenge: jsonArray(storage.getItem(KEYS.challenge), isString),
    grantedSeen: jsonArray(storage.getItem(KEYS.grantedSeen), isString),
    equipped: jsonArray(storage.getItem(KEYS.equipped), isString),
    evolved: jsonArray(storage.getItem(KEYS.evolved), isString),
    bagOrderedAt: Number.isFinite(bagN) ? bagN : null,
    bags: nonNegInt(storage.getItem(KEYS.bags), 0),
    scores,
    flags: jsonArray(storage.getItem(KEYS.flags), isFeatureFlag),
    // MAX_RANK, never a literal: a hard-coded 30 here outlived the Diamond
    // ranks (2026-10-29) and pushed every rank above 30 to the server as 30,
    // so a reload pulled the player back down.
    rank: Math.min(nonNegInt(storage.getItem(KEYS.rank), 1, 1), MAX_RANK),
    exp: nonNegInt(storage.getItem(KEYS.exp), 0),
    achievements: jsonArray(storage.getItem(KEYS.achievements), isAchievementId),
    losses: nonNegInt(storage.getItem(KEYS.losses), 0),
    waves: nonNegInt(storage.getItem(KEYS.waves), 0),
    orb: asOrbState(storage.getItem(KEYS.orb)),
    hub: Math.min(nonNegInt(storage.getItem(KEYS.hub), 0), HUB_LEVELS.length),
    diamond: asDiamondState(storage.getItem(KEYS.diamond)),
    moonstone: asMoonstoneState(storage.getItem(KEYS.moonstone)),
    debris: nonNegInt(storage.getItem(KEYS.debris), 0),
    capycoins: nonNegInt(storage.getItem(KEYS.capycoins), 0),
    capsuleOrderedAt: Number.isFinite(capsuleN) ? capsuleN : null,
    capsules: nonNegInt(storage.getItem(KEYS.capsules), 0),
    goldstone: nonNegInt(storage.getItem(KEYS.goldstone), 0),
    goldenBagOrderedAt: Number.isFinite(goldenN) ? goldenN : null,
    goldenBags: nonNegInt(storage.getItem(KEYS.goldenBags), 0),
    capystone: nonNegInt(storage.getItem(KEYS.capystone), 0),
    capyBest: capyBestOrNull(storage.getItem(KEYS.capyBest)),
    darkCoins: nonNegInt(storage.getItem(KEYS.darkCoins), 0),
    darkShards: nonNegInt(storage.getItem(KEYS.darkShards), 0),
    evilOrb: asEvilOrbState(storage.getItem(KEYS.evilOrb)),
    tokenOfShroom: asTokenOfShroomState(storage.getItem(KEYS.tokenOfShroom)),
    lightCoins: nonNegInt(storage.getItem(KEYS.lightCoins), 0),
    lightShards: nonNegInt(storage.getItem(KEYS.lightShards), 0),
    lightOrb: asLightOrbState(storage.getItem(KEYS.lightOrb)),
    darkChests: nonNegInt(storage.getItem(KEYS.darkChests), 0),
    lightChests: nonNegInt(storage.getItem(KEYS.lightChests), 0),
    shrooms: nonNegInt(storage.getItem(KEYS.shrooms), 0),
    snowshrooms: nonNegInt(storage.getItem(KEYS.snowshrooms), 0),
    effects: parseEffects(storage.getItem(KEYS.effects)),
    potions: parsePotions(storage.getItem(KEYS.potions)),
    potionShop: parsePotionShop(storage.getItem(KEYS.potionShop)),
    hubPeaceful: peacefulOrNull(storage.getItem(KEYS.hubPeaceful)),
    quests: parseQuests(storage.getItem(KEYS.quests)),
    stats: parseStats(storage.getItem(KEYS.stats)),
    avatarIcons: jsonArray(storage.getItem(KEYS.avatarIcons), isShopAvatarCode),
  };
}

/** The archive Capy Run best: a non-negative integer, or null (never finished). */
function capyBestOrNull(raw: string | null): number | null {
  const n = raw != null ? Number(raw) : NaN;
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/** The Peaceful record, or null for an absent/garbage key (never touched). */
function peacefulOrNull(raw: string | null): PeacefulState | null {
  if (raw == null) return null;
  const p = parsePeaceful(raw, HUB_LEVELS.length);
  return p.on || p.hub > 0 || Object.keys(p.stars).length > 0 ? p : null;
}

/** Write a server row over the synced keys (null bag order → key removed). */
export function applyServerSave(storage: StorageLike, row: GameSaveRow): void {
  const p = rowToPayload(row);
  storage.setItem(KEYS.stars, JSON.stringify(p.stars));
  storage.setItem(KEYS.unlocked, String(p.unlocked));
  storage.setItem(KEYS.coins, String(p.coins));
  storage.setItem(KEYS.entered, JSON.stringify(p.entered));
  storage.setItem(KEYS.owned, JSON.stringify(p.owned));
  storage.setItem(KEYS.granted, JSON.stringify(p.granted));
  storage.setItem(KEYS.challenge, JSON.stringify(p.challenge));
  storage.setItem(KEYS.grantedSeen, JSON.stringify(p.grantedSeen));
  storage.setItem(KEYS.equipped, JSON.stringify(p.equipped));
  storage.setItem(KEYS.evolved, JSON.stringify(p.evolved));
  if (p.bagOrderedAt == null) storage.removeItem(KEYS.bagOrderedAt);
  else storage.setItem(KEYS.bagOrderedAt, String(p.bagOrderedAt));
  storage.setItem(KEYS.bags, String(p.bags));
  storage.setItem(KEYS.scores, JSON.stringify(p.scores));
  storage.setItem(KEYS.flags, JSON.stringify(p.flags));
  storage.setItem(KEYS.rank, String(p.rank));
  storage.setItem(KEYS.exp, String(p.exp));
  storage.setItem(KEYS.achievements, JSON.stringify(p.achievements));
  storage.setItem(KEYS.losses, String(p.losses));
  storage.setItem(KEYS.waves, String(p.waves));
  // '' is "never claimed"; writing it would leave a meaningless empty string in
  // storage, so an unclaimed orb removes the key instead (matching bagOrderedAt).
  if (p.orb === '') storage.removeItem(KEYS.orb);
  else storage.setItem(KEYS.orb, p.orb);
  storage.setItem(KEYS.hub, String(p.hub));
  if (p.diamond === '') storage.removeItem(KEYS.diamond);
  else storage.setItem(KEYS.diamond, p.diamond);
  if (p.moonstone === '') storage.removeItem(KEYS.moonstone);
  else storage.setItem(KEYS.moonstone, p.moonstone);
  storage.setItem(KEYS.debris, String(p.debris));
  storage.setItem(KEYS.capycoins, String(p.capycoins));
  // Absent, not 0, when no capsule is spinning — the same treatment as
  // bagOrderedAt, so `capsuleOrderedAt()` reads null rather than epoch zero.
  if (p.capsuleOrderedAt == null) storage.removeItem(KEYS.capsuleOrderedAt);
  else storage.setItem(KEYS.capsuleOrderedAt, String(p.capsuleOrderedAt));
  storage.setItem(KEYS.capsules, String(p.capsules));
  storage.setItem(KEYS.goldstone, String(p.goldstone));
  // Absent, not 0, when no Golden bag is cooking — as for bagOrderedAt.
  if (p.goldenBagOrderedAt == null) storage.removeItem(KEYS.goldenBagOrderedAt);
  else storage.setItem(KEYS.goldenBagOrderedAt, String(p.goldenBagOrderedAt));
  storage.setItem(KEYS.goldenBags, String(p.goldenBags));
  storage.setItem(KEYS.capystone, String(p.capystone));
  // Absent, not 0, when no archive run has finished: 0 is a real score.
  if (p.capyBest == null) storage.removeItem(KEYS.capyBest);
  else storage.setItem(KEYS.capyBest, String(p.capyBest));
  storage.setItem(KEYS.darkCoins, String(p.darkCoins));
  storage.setItem(KEYS.darkShards, String(p.darkShards));
  if (p.evilOrb === '') storage.removeItem(KEYS.evilOrb);
  else storage.setItem(KEYS.evilOrb, p.evilOrb);
  if (p.tokenOfShroom === '') storage.removeItem(KEYS.tokenOfShroom);
  else storage.setItem(KEYS.tokenOfShroom, p.tokenOfShroom);
  storage.setItem(KEYS.lightCoins, String(p.lightCoins));
  storage.setItem(KEYS.lightShards, String(p.lightShards));
  if (p.lightOrb === '') storage.removeItem(KEYS.lightOrb);
  else storage.setItem(KEYS.lightOrb, p.lightOrb);
  storage.setItem(KEYS.darkChests, String(p.darkChests));
  storage.setItem(KEYS.lightChests, String(p.lightChests));
  storage.setItem(KEYS.shrooms, String(p.shrooms));
  storage.setItem(KEYS.snowshrooms, String(p.snowshrooms));
  storage.setItem(KEYS.effects, JSON.stringify(p.effects));
  storage.setItem(KEYS.potions, JSON.stringify(p.potions));
  if (p.potionShop == null) storage.removeItem(KEYS.potionShop);
  else storage.setItem(KEYS.potionShop, JSON.stringify(p.potionShop));
  if (p.hubPeaceful == null) storage.removeItem(KEYS.hubPeaceful);
  else storage.setItem(KEYS.hubPeaceful, JSON.stringify(p.hubPeaceful));
  // A null column means "never touched": remove the key rather than write an
  // empty record (the same treatment as bagOrderedAt and potionShop).
  if (row.quests == null) storage.removeItem(KEYS.quests);
  else storage.setItem(KEYS.quests, JSON.stringify(p.quests));
  if (row.stats == null) storage.removeItem(KEYS.stats);
  else storage.setItem(KEYS.stats, JSON.stringify(p.stats));
  storage.setItem(KEYS.avatarIcons, JSON.stringify(p.avatarIcons));
}

export function payloadToRow(p: SavePayload, userId: string): GameSaveRow {
  return {
    user_id: userId,
    save_version: p.saveVersion,
    coins: Math.min(p.coins, COIN_BALANCE_CAP),
    unlocked: p.unlocked,
    stars: p.stars,
    entered: p.entered,
    owned: p.owned,
    granted: p.granted,
    challenge: p.challenge,
    granted_seen: p.grantedSeen,
    equipped: p.equipped,
    evolved: p.evolved,
    bag_ordered_at: p.bagOrderedAt,
    bags: p.bags,
    scores: p.scores,
    flags: p.flags,
    rank: p.rank,
    exp: p.exp,
    achievements: p.achievements,
    losses: p.losses,
    waves: p.waves,
    orb: p.orb,
    hub: p.hub,
    diamond: p.diamond,
    moonstone: p.moonstone,
    debris: p.debris,
    capycoins: p.capycoins,
    capsule_ordered_at: p.capsuleOrderedAt,
    capsules: p.capsules,
    goldstone: p.goldstone,
    golden_bag_ordered_at: p.goldenBagOrderedAt,
    golden_bags: p.goldenBags,
    capystone: p.capystone,
    capy_best: p.capyBest,
    dark_coins: Math.min(p.darkCoins, COIN_BALANCE_CAP),
    dark_shards: Math.min(p.darkShards, COIN_BALANCE_CAP),
    evil_orb: p.evilOrb,
    token_of_shroom: p.tokenOfShroom,
    light_coins: Math.min(p.lightCoins, COIN_BALANCE_CAP),
    light_shards: Math.min(p.lightShards, COIN_BALANCE_CAP),
    light_orb: p.lightOrb,
    dark_chests: Math.min(p.darkChests, COIN_BALANCE_CAP),
    light_chests: Math.min(p.lightChests, COIN_BALANCE_CAP),
    shrooms: Math.min(p.shrooms, COIN_BALANCE_CAP),
    snowshrooms: Math.min(p.snowshrooms, COIN_BALANCE_CAP),
    effects: p.effects,
    potions: p.potions,
    potion_shop: p.potionShop,
    hub_peaceful: p.hubPeaceful,
    quests: p.quests,
    stats: p.stats,
    avatar_icons: p.avatarIcons,
  };
}

/** Row → payload, re-validating every field (the row crosses a trust line). */
export function rowToPayload(row: GameSaveRow): SavePayload {
  return {
    saveVersion: isNumber(row.save_version) ? row.save_version : SAVE_VERSION,
    coins: isNumber(row.coins) && row.coins >= 0 ? row.coins : 0,
    unlocked: isNumber(row.unlocked) && row.unlocked >= 1 ? row.unlocked : 1,
    stars:
      row.stars && typeof row.stars === 'object' && !Array.isArray(row.stars)
        ? (Object.fromEntries(Object.entries(row.stars).filter(([, v]) => isNumber(v))) as Record<
            string,
            number
          >)
        : {},
    entered: Array.isArray(row.entered) ? row.entered.filter(isNumber) : [],
    owned: Array.isArray(row.owned) ? row.owned.filter(isString) : [],
    granted: Array.isArray(row.granted) ? row.granted.filter(isString) : [],
    challenge: Array.isArray(row.challenge) ? row.challenge.filter(isString) : [],
    grantedSeen: Array.isArray(row.granted_seen) ? row.granted_seen.filter(isString) : [],
    equipped: Array.isArray(row.equipped) ? row.equipped.filter(isString) : [],
    evolved: Array.isArray(row.evolved) ? row.evolved.filter(isString) : [],
    bagOrderedAt: isNumber(row.bag_ordered_at) ? row.bag_ordered_at : null,
    bags: isNumber(row.bags) && row.bags >= 0 ? row.bags : 0,
    scores: Array.isArray(row.scores) ? row.scores.filter(isScore) : [],
    flags: Array.isArray(row.flags) ? row.flags.filter(isFeatureFlag) : [],
    rank:
      isNumber(row.rank) && Number.isInteger(row.rank) && row.rank >= 1 && row.rank <= MAX_RANK
        ? row.rank
        : 1,
    exp: isNumber(row.exp) && Number.isInteger(row.exp) && row.exp >= 0 ? row.exp : 0,
    achievements: Array.isArray(row.achievements) ? row.achievements.filter(isAchievementId) : [],
    losses:
      isNumber(row.losses) && Number.isInteger(row.losses) && row.losses >= 0 ? row.losses : 0,
    waves: isNumber(row.waves) && Number.isInteger(row.waves) && row.waves >= 0 ? row.waves : 0,
    orb: asOrbState(isString(row.orb) ? row.orb : null),
    hub:
      isNumber(row.hub) && Number.isInteger(row.hub) && row.hub >= 0
        ? Math.min(row.hub, HUB_LEVELS.length)
        : 0,
    diamond: asDiamondState(isString(row.diamond) ? row.diamond : null),
    moonstone: asMoonstoneState(isString(row.moonstone) ? row.moonstone : null),
    debris:
      isNumber(row.debris) && Number.isInteger(row.debris) && row.debris >= 0 ? row.debris : 0,
    capycoins:
      isNumber(row.capycoins) && Number.isInteger(row.capycoins) && row.capycoins >= 0
        ? row.capycoins
        : 0,
    capsuleOrderedAt: isNumber(row.capsule_ordered_at) ? row.capsule_ordered_at : null,
    capsules: isNumber(row.capsules) && row.capsules >= 0 ? row.capsules : 0,
    goldstone:
      isNumber(row.goldstone) && Number.isInteger(row.goldstone) && row.goldstone >= 0
        ? row.goldstone
        : 0,
    goldenBagOrderedAt: isNumber(row.golden_bag_ordered_at) ? row.golden_bag_ordered_at : null,
    goldenBags:
      isNumber(row.golden_bags) && Number.isInteger(row.golden_bags) && row.golden_bags >= 0
        ? row.golden_bags
        : 0,
    capystone:
      isNumber(row.capystone) && Number.isInteger(row.capystone) && row.capystone >= 0
        ? row.capystone
        : 0,
    capyBest:
      isNumber(row.capy_best) && Number.isInteger(row.capy_best) && row.capy_best >= 0
        ? row.capy_best
        : null,
    darkCoins:
      isNumber(row.dark_coins) && Number.isInteger(row.dark_coins) && row.dark_coins >= 0
        ? row.dark_coins
        : 0,
    darkShards:
      isNumber(row.dark_shards) && Number.isInteger(row.dark_shards) && row.dark_shards >= 0
        ? row.dark_shards
        : 0,
    evilOrb: asEvilOrbState(isString(row.evil_orb) ? row.evil_orb : null),
    tokenOfShroom: asTokenOfShroomState(isString(row.token_of_shroom) ? row.token_of_shroom : null),
    lightCoins:
      isNumber(row.light_coins) && Number.isInteger(row.light_coins) && row.light_coins >= 0
        ? row.light_coins
        : 0,
    lightShards:
      isNumber(row.light_shards) && Number.isInteger(row.light_shards) && row.light_shards >= 0
        ? row.light_shards
        : 0,
    lightOrb: asLightOrbState(isString(row.light_orb) ? row.light_orb : null),
    darkChests:
      isNumber(row.dark_chests) && Number.isInteger(row.dark_chests) && row.dark_chests >= 0
        ? row.dark_chests
        : 0,
    lightChests:
      isNumber(row.light_chests) && Number.isInteger(row.light_chests) && row.light_chests >= 0
        ? row.light_chests
        : 0,
    shrooms:
      isNumber(row.shrooms) && Number.isInteger(row.shrooms) && row.shrooms >= 0 ? row.shrooms : 0,
    snowshrooms:
      isNumber(row.snowshrooms) && Number.isInteger(row.snowshrooms) && row.snowshrooms >= 0
        ? row.snowshrooms
        : 0,
    // The jsonb columns come back as parsed values; re-serialising lets the
    // config parsers re-validate them exactly as they validate localStorage.
    effects: parseEffects(row.effects == null ? null : JSON.stringify(row.effects)),
    potions: parsePotions(row.potions == null ? null : JSON.stringify(row.potions)),
    potionShop: parsePotionShop(row.potion_shop == null ? null : JSON.stringify(row.potion_shop)),
    hubPeaceful: peacefulOrNull(row.hub_peaceful == null ? null : JSON.stringify(row.hub_peaceful)),
    quests: parseQuests(row.quests == null ? null : JSON.stringify(row.quests)),
    stats: parseStats(row.stats == null ? null : JSON.stringify(row.stats)),
    avatarIcons: Array.isArray(row.avatar_icons) ? row.avatar_icons.filter(isShopAvatarCode) : [],
  };
}

/**
 * "No meaningful progress": what a brand-new player's save looks like.
 * equipped/grantedSeen/scores deliberately don't count — they can be non-empty
 * without the player having earned anything worth protecting.
 */
export function isFreshSave(p: SavePayload): boolean {
  return (
    (p.hubPeaceful == null || p.hubPeaceful.hub === 0) &&
    p.coins === 0 &&
    p.unlocked <= 1 &&
    Object.keys(p.stars).length === 0 &&
    p.entered.length === 0 &&
    p.owned.length === 0 &&
    p.granted.length === 0 &&
    p.challenge.length === 0 &&
    p.evolved.length === 0 &&
    p.bagOrderedAt == null &&
    p.bags === 0 &&
    p.flags.length === 0 &&
    p.rank <= 1 &&
    p.exp === 0 &&
    p.achievements.length === 0 &&
    p.losses === 0 &&
    p.waves === 0 &&
    p.orb === '' &&
    p.hub === 0 &&
    p.diamond === '' &&
    p.moonstone === '' &&
    p.debris === 0 &&
    p.capycoins === 0 &&
    p.capsuleOrderedAt == null &&
    p.capsules === 0 &&
    p.goldstone === 0 &&
    p.goldenBagOrderedAt == null &&
    p.goldenBags === 0 &&
    p.capystone === 0 &&
    p.capyBest == null &&
    p.darkCoins === 0 &&
    p.darkShards === 0 &&
    p.evilOrb === '' &&
    p.tokenOfShroom === '' &&
    p.lightCoins === 0 &&
    p.lightShards === 0 &&
    p.lightOrb === '' &&
    p.darkChests === 0 &&
    p.lightChests === 0 &&
    p.shrooms === 0 &&
    p.snowshrooms === 0 &&
    p.effects.length === 0 &&
    p.potions.length === 0 &&
    p.avatarIcons.length === 0
  );
}

/** Remove every synced key — logout's "back to the beginning". */
export function wipeLocalSave(storage: StorageLike): void {
  for (const k of SYNCED_KEYS) storage.removeItem(k);
}

export const DEBOUNCE_MS = 3000;
const TABLE = 'game_saves';

/**
 * Write-through sync while a session exists. Last write wins everywhere:
 * pushes upsert the FULL payload, pulls overwrite the full local set.
 * All server errors are swallowed after a console.warn — save sync must
 * never break gameplay.
 */
export class SaveSyncEngine {
  private userId: string | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly client: SupabaseClient,
    private readonly storage: StorageLike,
    private readonly reload: () => void = () => window.location.reload(),
  ) {}

  /**
   * Session restore: with a live session, make local mirror the account.
   * Freshness-aware, mirroring syncOnLogin: a FRESH server save is never
   * authoritative (the migration backfills empty rows for pre-feature
   * accounts — applying one would wipe a signed-in player's real progress),
   * and a fresh local save is never pushed (it would overwrite nothing with
   * nothing, or race another device's first load). Runs BEFORE the game
   * boots so scenes construct from the account state.
   */
  async init(): Promise<void> {
    const { data } = await this.client.auth.getSession();
    this.userId = data.session?.user.id ?? null;
    this.client.auth.onAuthStateChange((_event, session) => {
      this.userId = session?.user.id ?? null;
    });
    if (!this.userId) return;
    try {
      const row = await this.fetchRow();
      const local = collectLocalSave(this.storage);
      if (row && !isFreshSave(rowToPayload(row))) applyServerSave(this.storage, row);
      else if (!isFreshSave(local)) await this.pushNow();
    } catch (e) {
      console.warn('save sync: session restore failed', e);
    }
  }

  /** A synced key changed: schedule a debounced full-payload push. */
  notifyWrite(): void {
    if (!this.userId) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), DEBOUNCE_MS);
  }

  /** Push immediately (debounce timer cancelled). */
  async flush(): Promise<void> {
    clearTimeout(this.timer);
    this.timer = undefined;
    if (!this.userId) return;
    try {
      await this.pushNow();
    } catch (e) {
      console.warn('save sync: push failed', e);
    }
  }

  /**
   * The tab is going away: push ONLY if a write is still waiting out its
   * debounce. Pushing unconditionally re-sent the whole local save on every
   * navigation and tab close — which overwrote anything changed on the server
   * since this page last pulled (an admin's SQL edit, which is how saves are
   * edited by hand), and raced the auth e2e that seeds a save by SQL and then
   * navigates (leaderboard.spec, red on CI 2026-10-08).
   */
  async flushPending(): Promise<void> {
    if (this.timer === undefined) return;
    await this.flush();
  }

  /**
   * Interactive login just succeeded. Fresh server save + local progress →
   * the account inherits the local save (nothing destroyed). Otherwise the
   * server is the truth (rule: login overwrites local). Reloads the page so
   * every scene re-reads storage.
   */
  async syncOnLogin(): Promise<void> {
    const { data } = await this.client.auth.getSession();
    this.userId = data.session?.user.id ?? null;
    if (!this.userId) return;
    try {
      const row = await this.fetchRow();
      const local = collectLocalSave(this.storage);
      if ((row == null || isFreshSave(rowToPayload(row))) && !isFreshSave(local)) {
        await this.pushNow();
      } else if (row) {
        applyServerSave(this.storage, row);
      }
    } catch (e) {
      console.warn('save sync: login sync failed', e);
    }
    this.reload();
  }

  /**
   * About to log out: cancel any pending debounce and push a final snapshot
   * so the account keeps the latest progress. MUST complete before the local
   * wipe — a push after the wipe could write an empty save over the account.
   */
  async syncBeforeLogout(): Promise<void> {
    await this.flush();
  }

  localIsFresh(): boolean {
    return isFreshSave(collectLocalSave(this.storage));
  }

  private async fetchRow(): Promise<GameSaveRow | null> {
    const { data, error } = await this.client
      .from(TABLE)
      .select('*')
      .eq('user_id', this.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (data as GameSaveRow | null) ?? null;
  }

  private async pushNow(): Promise<void> {
    if (!this.userId) return;
    const row = payloadToRow(collectLocalSave(this.storage), this.userId);
    const { error } = await this.client.from(TABLE).upsert(row);
    if (error) throw new Error(error.message);
  }
}

/**
 * Storage decorator: pass every operation through, and after a write or
 * removal of a SYNCED key, tell the sync engine. Reads are untouched —
 * localStorage stays the game's source of truth; the server is the replica.
 */
export function createSyncedStorage(base: StorageLike, onSyncedWrite: () => void): StorageLike {
  const synced = new Set(SYNCED_KEYS);
  return {
    getItem: (k) => base.getItem(k),
    setItem: (k, v) => {
      base.setItem(k, v);
      if (synced.has(k)) onSyncedWrite();
    },
    removeItem: (k) => {
      base.removeItem(k);
      if (synced.has(k)) onSyncedWrite();
    },
  };
}

// ---- module singleton (wired in main.ts; inert when unconfigured) ----

let engine: SaveSyncEngine | null = null;

/** Create the singleton engine over real localStorage and run session restore. */
export async function initSaveSync(client: SupabaseClient): Promise<void> {
  engine = new SaveSyncEngine(client, window.localStorage);
  await engine.init();
  // Best-effort final push when the tab goes away MID-DEBOUNCE — and only then
  // (see flushPending).
  addEventListener('pagehide', () => void engine?.flushPending());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void engine?.flushPending();
  });
}

export function saveSyncEngine(): SaveSyncEngine | null {
  return engine;
}

/** Proxy for saveStorage()'s decorator — no-op until initSaveSync ran. */
export function notifySavedKeyWrite(): void {
  engine?.notifyWrite();
}
